import { useEffect, useRef, useState } from "react";
// Type-only import — erased at build time. The runtime module is loaded lazily
// via import("hls.js") only when native HLS is unavailable, keeping ~450 KB out
// of the entry bundle (Safari/iOS never downloads it).
import type Hls from "hls.js";

// Safari native live HLS reports duration=Infinity — assigning currentTime from it
// throws. Prefer the end of the seekable range when duration is not finite.
function seekNearEnd(video: HTMLVideoElement) {
    if (Number.isFinite(video.duration)) {
        video.currentTime = Math.max(0, video.duration - 5);
    } else if (video.seekable.length > 0) {
        video.currentTime = Math.max(0, video.seekable.end(video.seekable.length - 1) - 5);
    }
    // else: no finite duration and nothing seekable yet — skip the seek
}

export function Player({ streamKey, title, isLive, seekRef }: {
    streamKey: string;
    title: string;
    isLive?: boolean;
    /** StreamCard wires the chunk map's absolute-time seeks through this. */
    seekRef?: { current: (time: number) => void };
}) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [info, setInfo] = useState("");
    // "window": sliding live window at the edge (default for live streams).
    // "full": static VOD snapshot of the whole history (?full=1) — used for
    // watch-from-start and for absolute-time seeks into a live stream. The
    // windowed playlist's timeline doesn't start at t=0, so absolute seeks
    // only make sense against the full snapshot.
    const [mode, setMode] = useState<"window" | "full">("window");
    // Absolute time to seek to once the (re)loaded playlist is ready.
    const pendingSeekRef = useRef<number | null>(null);

    const liveWindow = !!isLive && mode === "window";

    useEffect(() => {
        const video = videoRef.current!;
        const fullSuffix = isLive && mode === "full" ? "?full=1" : "";
        const playlistUrl = `/api/play/${encodeURIComponent(streamKey)}.m3u8${fullSuffix}`;
        let destroyed = false;

        if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

        const onReady = () => {
            if (destroyed) return;
            video.play().catch(() => {});
            const pending = pendingSeekRef.current;
            pendingSeekRef.current = null;
            if (pending != null) video.currentTime = pending;
            else if (liveWindow) {
                const hls = hlsRef.current;
                if (hls?.liveSyncPosition) video.currentTime = hls.liveSyncPosition;
                else seekNearEnd(video);
            }
        };

        // Prefer Safari's native HLS — handles large playlists better than HLS.js
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = playlistUrl;
            video.addEventListener("loadedmetadata", onReady);
        } else {
            // No native HLS — lazy-load hls.js so it never ships in the entry bundle
            import("hls.js").then(({ default: Hls }) => {
                if (destroyed || !Hls.isSupported()) return;
                const hls = new Hls({
                    liveSyncDurationCount: 3,
                    liveMaxLatencyDurationCount: Infinity,
                    liveDurationInfinity: false,
                    maxBufferLength: 30,
                    maxMaxBufferLength: 600,
                    maxBufferHole: 0.5,
                    backBufferLength: 1800,
                    startFragPrefetch: false,
                });
                hlsRef.current = hls;
                hls.loadSource(playlistUrl);
                hls.attachMedia(video);
                hls.on(Hls.Events.MANIFEST_PARSED, onReady);
                hls.on(Hls.Events.ERROR, (_event, data) => {
                    console.warn(`[hls.js] ${data.type}/${data.details}`, data.fatal ? "FATAL" : "", data.reason || "");
                    if (data.fatal) {
                        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
                        else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
                    }
                });
                hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
                    if (!destroyed) setInfo(`loading seg ${data.frag.sn}`);
                });
            });
        }

        return () => {
            destroyed = true;
            video.removeEventListener("loadedmetadata", onReady);
            hlsRef.current?.destroy();
            hlsRef.current = null;
            // Safari keeps fetching playlist refreshes + segments for a detached
            // <video> with an HLS src — downloads continue after the player closes
            // unless the resource load is explicitly torn down.
            video.pause();
            video.removeAttribute("src");
            video.load();
        };
    }, [streamKey, mode, isLive]);

    // Absolute-time seek from the chunk map. On the live window the timeline
    // doesn't map to absolute time, so switch to the full snapshot first and
    // seek once it loads.
    const seekTo = (time: number) => {
        if (liveWindow) {
            pendingSeekRef.current = time;
            setMode("full");
        } else if (videoRef.current) {
            videoRef.current.currentTime = time;
        }
    };
    useEffect(() => { if (seekRef) seekRef.current = seekTo; });

    const seekToLive = () => {
        if (isLive && mode === "full") {
            // Reload the sliding-window playlist; onReady positions at the edge
            pendingSeekRef.current = null;
            setMode("window");
            return;
        }
        const video = videoRef.current;
        const hls = hlsRef.current;
        if (hls?.liveSyncPosition) video!.currentTime = hls.liveSyncPosition;
        else if (video) seekNearEnd(video);
    };

    const watchFromStart = () => {
        pendingSeekRef.current = 0;
        setMode("full");
    };

    const fmt = (s: number) => {
        if (!s || !isFinite(s)) return "0:00";
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = Math.floor(s % 60);
        return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
    };

    return (
        <div className="player-wrap">
            <h3>player - {title}</h3>
            <video
                id="playerVideo"
                ref={videoRef}
                controls
                playsInline
                onTimeUpdate={() => {
                    const v = videoRef.current!;
                    setInfo(liveWindow ? `${fmt(v.currentTime)} (live)` : `${fmt(v.currentTime)} / ${fmt(v.duration)}`);
                }}
            >
                {/* Announcer captions — empty VTT if not transcribed yet */}
                <track
                    kind="subtitles"
                    srcLang="en"
                    label="Announcer"
                    default={!isLive}
                    src={`/api/captions/${encodeURIComponent(streamKey)}.vtt`}
                />
            </video>
            <div className="player-controls">
                <button onClick={() => videoRef.current?.paused ? videoRef.current.play().catch(() => {}) : videoRef.current?.pause()}>play/pause</button>
                {isLive && liveWindow && <button onClick={watchFromStart}>from start</button>}
                {isLive && <button onClick={seekToLive}>live</button>}
                <span className="player-info">{info}</span>
            </div>
        </div>
    );
}
